/* eslint-disable guard-for-in */
/* eslint-disable prefer-promise-reject-errors */

import { notification } from '@edulastic/common'
import { playerSkinValues } from '@edulastic/constants/const/test'

import * as Sentry from '@sentry/browser'

import { Howl, Howler } from 'howler'
import React, { useImperativeHandle, useEffect, useRef, useState } from 'react'

import { withNamespaces } from '@edulastic/localization'
import Controls from './Controls'

const { edulastic, drc, parcc, cmas, sbac } = playerSkinValues
const showText = [edulastic, sbac]

const TestAudioControls = ({
  test,
  audioSrc,
  className,
  hideVisibility,
  isPremiumContentWithoutAccess = false,
  ttsPlaybackSpeed = '1',
  playerSkinType = 'edulastic',
  isStudentReport = false,
  controlRef,
  isMinimalDesign,
  t: i18Translate,
  padding,
  buttonTexts = {},
  languageDropdownSelector = null,
}) => {
  /**
   * Use custom button text when both english and spanish audio when stacked translation enabled.
   * No dependency on proffered language as both button are visible
   */
  const { _id: testId, showInstructionAcknowledgement } = test

  // Local state for current playing details
  const [currentPlayingDetails, setCurrentPlayingDetails] = useState({
    qId: '',
  })

  const resetCurrentPlayingDetails = () => {
    setCurrentPlayingDetails({
      qId: '',
    })
  }

  const {
    play,
    pause,
    stop,
    ttsProcessingInProgress,
    ttsNotSupported,
  } = buttonTexts
  const [loading, setLoading] = useState(true)
  const [instructionHowl, setInstructionHowl] = useState(null)
  const [
    instructionAcknowledgementHowl,
    setInstructionAcknowledgementHowl,
  ] = useState(null)

  // Start of common functions
  const [currentHowl, setCurrentHowl] = useState({})

  const btnWithText = showText.includes(playerSkinType?.toLowerCase())

  const ttsAudioPlaybackRate = parseFloat(
    [edulastic, drc, cmas, parcc].includes(playerSkinType) && !isStudentReport
      ? ttsPlaybackSpeed
      : '1'
  )

  const ttsAudioPlaybackRateRef = useRef(ttsAudioPlaybackRate)

  useEffect(() => {
    const findAllPlayingHowls = Howler._howls.filter((item) => item.playing())
    /** Ref is needed to change audio rate for already loaded audio */
    if (ttsAudioPlaybackRateRef?.current) {
      ttsAudioPlaybackRateRef.current = ttsAudioPlaybackRate
    }

    if (findAllPlayingHowls.length) {
      findAllPlayingHowls.forEach((item) => item.rate(ttsAudioPlaybackRate))
    }
  }, [ttsAudioPlaybackRate])

  // Loading audio
  const audioLoadResolve = (url) => {
    const _prom = new Promise((resolve, reject) => {
      const srcArr = [url].filter((a) => a)

      if (!srcArr.length) {
        reject([4, 'Tried to initialize howl with empty urls'], url)
      }

      const sound = new Howl({
        src: srcArr,
        preload: false,
        html5: true,
        rate: ttsAudioPlaybackRateRef.current || 1,
      })

      resolve(sound)

      sound.on('playerror', (...args) => {
        reject({ args, url })
      })
      sound.on('loaderror', (...args) => {
        reject({ args, url })
      })
    })

    _prom.catch((err) => {
      setLoading(false)
      Sentry.withScope((scope) => {
        scope.setExtra('error', err)
        notification({ type: 'error', messageKey: 'ttsErrorMessage' })
        Sentry.captureException(
          new Error('[AudioControls] audio load failure.')
        )
      })
    })

    return _prom
  }

  // Playing audio
  const audioPlayResolve = (_howl) => {
    if (!_howl) return
    _howl?.rate(ttsAudioPlaybackRateRef?.current || 1)
    const _prom = new Promise((resolve, reject) => {
      _howl?.load()
      if (_howl?.state() === 'loading' || _howl?.state() === 'unloaded') {
        _howl.on('load', () => {
          if (!_howl?.playing(_howl?._idRef)) _howl._idRef = _howl?.play()
        })
      } else if (!_howl?.playing(_howl?._idRef)) _howl._idRef = _howl?.play()

      _howl?.on('playerror', (...args) => {
        reject({ args })
      })

      setCurrentHowl(_howl)
      _howl?.once('end', () => {
        resolve(_howl)
      })
    })

    _prom.catch((err) => {
      setLoading(false)
      Sentry.withScope((scope) => {
        scope.setExtra('error', err)
        notification({ type: 'error', messageKey: 'ttsErrorMessage' })
        Sentry.captureException(
          new Error('[AudioControls] audio playing failure.')
        )
      })
    })

    return _prom
  }

  const stopAllAudios = () => {
    const findAllPlayingHowls = Howler._howls.filter((item) => item.playing())
    if (findAllPlayingHowls.length) {
      findAllPlayingHowls.forEach((item) => item.stop())
    }
  }
  useEffect(() => {
    const isSupported = Howler.codecs('mp3')

    if (!isSupported) {
      notification({ type: 'error', msg: 'Audio format is not supported.' })
      Sentry.captureException(new Error('[AudioControls] Mp3 not supported.'))
    }
  }, [])
  // End of common functions

  useEffect(() => {
    if (!audioSrc) return
    audioLoadResolve(audioSrc).then((sound) => {
      setInstructionHowl(sound)
      setCurrentHowl(sound)
      const instructionAcknowledgementAudioSrc =
        test?.testTTS?.instructionAcknowledgementText
          ?.instructionAcknowledgementTextAudioURL
      if (
        instructionAcknowledgementAudioSrc &&
        showInstructionAcknowledgement
      ) {
        audioLoadResolve(instructionAcknowledgementAudioSrc).then((sound2) => {
          setInstructionAcknowledgementHowl(sound2)
          setLoading(false)
        })
      } else {
        setLoading(false)
      }
    })
    return () => {
      resetCurrentPlayingDetails()
      stopAllAudios()
      Howler.unload()
    }
  }, [testId, audioSrc])

  const handleStopAudio = () => {
    currentHowl?.stop()
    resetCurrentPlayingDetails()
  }

  const handlePlayPauseAudio = () => {
    if (loading || !currentHowl) {
      return
    }

    currentHowl.rate(ttsAudioPlaybackRateRef?.current)
    if (currentHowl?.playing()) {
      currentHowl.pause()
      currentHowl.isPaused = true
      return resetCurrentPlayingDetails()
    }
    if (currentHowl?.isPaused) {
      currentHowl.play()
      currentHowl.isPaused = false
      return setCurrentPlayingDetails({ qId: testId })
    }
    handleStopAudio()
    stopAllAudios()
    setCurrentPlayingDetails({ qId: testId })
    if (!instructionHowl) return
    audioPlayResolve(instructionHowl).then(() => {
      const asyncPlay = async () => {
        if (instructionAcknowledgementHowl) {
          await audioPlayResolve(instructionAcknowledgementHowl)
          resetCurrentPlayingDetails()
        } else {
          Sentry.captureMessage(
            `[AudioControls] Instruction acknowledgement audio missing`,
            'info'
          )
          resetCurrentPlayingDetails()
        }
      }
      asyncPlay()
    })
  }

  useImperativeHandle(controlRef, () => ({
    play: handlePlayPauseAudio,
    stop: handleStopAudio,
  }))

  const isSupported = Howler.codecs('mp3')

  const playPauseToolTip = loading
    ? ttsProcessingInProgress ||
      i18Translate('common.test.ttsButton.ttsProcessingInProgress')
    : currentPlayingDetails?.qId === testId
    ? pause || i18Translate('common.test.ttsButton.pause')
    : play || i18Translate('common.test.ttsButton.play')

  return (
    <Controls
      btnWithText={btnWithText}
      hideVisibility={hideVisibility}
      className={className}
      padding={padding}
      languageDropdownSelector={languageDropdownSelector}
      isSupported={isSupported}
      loading={loading}
      handlePlayPauseAudio={handlePlayPauseAudio}
      handleStopAudio={handleStopAudio}
      playPauseToolTip={playPauseToolTip}
      play={play}
      pause={pause}
      stop={stop}
      isPremiumContentWithoutAccess={isPremiumContentWithoutAccess}
      isMinimalDesign={isMinimalDesign}
      playerSkinType={playerSkinType}
      currentPlayingDetails={currentPlayingDetails}
      qId={testId}
      ttsNotSupported={ttsNotSupported}
      t={i18Translate}
    />
  )
}

export default withNamespaces('student')(TestAudioControls)
